'use client';

/**
 * Organisation Selector Modal
 * 
 * A reusable modal for selecting organisations with hierarchical display.
 * Shows parent-child relationships with visual indentation and search.
 */
import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from 'react';
import { Building2, Search, Check } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { OrganizationWithRole } from '@/types/organization';
interface OrgWithDepth extends OrganizationWithRole {
  depth: number;
  hasChildren: boolean;
}
interface OrgSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizations: OrganizationWithRole[];
  selectedOrgId?: string | null;
  onSelect: (org: OrganizationWithRole) => void;
  title?: string;
  description?: string;
}

// Build hierarchical list with depth info
function buildHierarchy(orgs: OrganizationWithRole[]): OrgWithDepth[] {
  const result: OrgWithDepth[] = [];
  const orgMap = new Map(orgs.map(org => [org.id, org]));
  const childrenMap = new Map<string | null, OrganizationWithRole[]>();

  // Group orgs by parent
  orgs.forEach(org => {
    const parentId = org.parent_org_id || null;
    if (!childrenMap.has(parentId)) {
      childrenMap.set(parentId, []);
    }
    childrenMap.get(parentId)!.push(org);
  });

  // Sort children alphabetically
  childrenMap.forEach(children => {
    children.sort((a, b) => a.name.localeCompare(b.name));
  });

  // Recursively add orgs with depth
  const addOrgWithChildren = (org: OrganizationWithRole, depth: number) => {
    const children = childrenMap.get(org.id) || [];
    result.push({
      ...org,
      depth,
      hasChildren: children.length > 0
    });
    children.forEach(child => addOrgWithChildren(child, depth + 1));
  };

  // Start with root orgs (no parent or parent not in list)
  const rootOrgs = orgs.filter(org => !org.parent_org_id || !orgMap.has(org.parent_org_id));
  rootOrgs.sort((a, b) => a.name.localeCompare(b.name));
  rootOrgs.forEach(org => addOrgWithChildren(org, 0));
  return result;
}
export function OrgSelectorModal(t0) {
  const $ = _c(36);
  const {
    open,
    onOpenChange,
    organizations,
    selectedOrgId,
    onSelect,
    title: t1,
    description: t2
  } = t0;
  const title = t1 === undefined ? "Switch Organisation" : t1;
  const description = t2 === undefined ? "Select an organisation to work with" : t2;
  const [searchQuery, setSearchQuery] = useState("");
  let t3;
  if ($[0] !== organizations || $[1] !== searchQuery) {
    bb0: {
      const hierarchy = buildHierarchy(organizations);
      if (!searchQuery.trim()) {
        t3 = hierarchy;
        break bb0;
      }
      const query = searchQuery.toLowerCase();
      t3 = hierarchy.filter(org => org.name.toLowerCase().includes(query) || org.slug?.toLowerCase().includes(query) || org.brand_name?.toLowerCase().includes(query));
    }
    $[0] = organizations;
    $[1] = searchQuery;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const displayOrgs = t3;
  let t4;
  if ($[3] !== onOpenChange || $[4] !== onSelect) {
    t4 = org_0 => {
      onSelect(org_0);
      onOpenChange(false);
      setSearchQuery("");
    };
    $[3] = onOpenChange;
    $[4] = onSelect;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleSelect = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Building2 className="h-5 w-5" />;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== title) {
    t6 = <DialogTitle className="flex items-center gap-2">{t5}{title}</DialogTitle>;
    $[7] = title;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== description) {
    t7 = <DialogDescription>{description}</DialogDescription>;
    $[9] = description;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t6 || $[12] !== t7) {
    t8 = <DialogHeader className="pb-4 border-b border-white/10">{t6}{t7}</DialogHeader>;
    $[11] = t6;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = e => setSearchQuery(e.target.value);
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== searchQuery) {
    t11 = <div className="relative">{t9}<Input placeholder="Search organisations..." value={searchQuery} onChange={t10} className="pl-10" autoFocus={true} /></div>;
    $[16] = searchQuery;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== displayOrgs || $[19] !== handleSelect || $[20] !== selectedOrgId) {
    t12 = displayOrgs.length === 0 ? <div className="text-center py-8 text-muted-foreground"><Building2 className="h-8 w-8 mx-auto mb-2 opacity-50" /><p className="text-sm">No organisations found</p></div> : displayOrgs.map(org_1 => <OrgItem key={org_1.id} org={org_1} isSelected={org_1.id === selectedOrgId} onSelect={() => handleSelect(org_1)} />);
    $[18] = displayOrgs;
    $[19] = handleSelect;
    $[20] = selectedOrgId;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = <div className="max-h-[400px] overflow-y-auto -mx-6 px-6"><div className="space-y-1 py-2">{t12}</div></div>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== displayOrgs.length || $[25] !== organizations.length) {
    t14 = <div className="text-xs text-muted-foreground text-center pt-2 border-t border-white/10">{displayOrgs.length} of {organizations.length} organisations</div>;
    $[24] = displayOrgs.length;
    $[25] = organizations.length;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t11 || $[28] !== t13 || $[29] !== t14 || $[30] !== t8) {
    t15 = <DialogContent className="glass-strong border border-white/10 sm:max-w-md">{t8}{t11}{t13}{t14}</DialogContent>;
    $[27] = t11;
    $[28] = t13;
    $[29] = t14;
    $[30] = t8;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== onOpenChange || $[33] !== open || $[34] !== t15) {
    t16 = <Dialog open={open} onOpenChange={onOpenChange}>{t15}</Dialog>;
    $[32] = onOpenChange;
    $[33] = open;
    $[34] = t15;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  return t16;
}

// Individual org item with hierarchy styling
interface OrgItemProps {
  org: OrgWithDepth;
  isSelected: boolean;
  onSelect: () => void;
}
function OrgItem(t0) {
  const $ = _c(35);
  const {
    org,
    isSelected,
    onSelect
  } = t0;
  const t1 = `w-full text-left px-3 py-2.5 rounded-lg transition-all hover:bg-accent/10 ${isSelected ? "bg-accent/15 ring-1 ring-primary" : ""}`;
  const t2 = `${12 + org.depth * 24}px`;
  let t3;
  if ($[0] !== t2) {
    t3 = {
      paddingLeft: t2
    };
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== org.depth) {
    t4 = org.depth > 0 && <span className="text-muted-foreground text-sm -ml-5">↳</span>;
    $[2] = org.depth;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== org.avatar_logo_url || $[5] !== org.depth || $[6] !== org.name || $[7] !== org.primary_color || $[8] !== org.secondary_color) {
    t5 = org.avatar_logo_url ? <div className={`${org.depth > 0 ? "h-7 w-7" : "h-9 w-9"} rounded-full overflow-hidden flex-shrink-0 border bg-white`}><img src={org.avatar_logo_url} alt={org.name} className="h-full w-full object-cover" /></div> : <div className={`${org.depth > 0 ? "h-7 w-7" : "h-9 w-9"} rounded-full flex items-center justify-center flex-shrink-0`} style={{
      background: `linear-gradient(135deg, ${org.primary_color || "#6366f1"}, ${org.secondary_color || "#818cf8"})`
    }}><Building2 className={`${org.depth > 0 ? "h-3.5 w-3.5" : "h-4 w-4"} text-white`} /></div>;
    $[4] = org.avatar_logo_url;
    $[5] = org.depth;
    $[6] = org.name;
    $[7] = org.primary_color;
    $[8] = org.secondary_color;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const t6 = `font-medium truncate ${org.depth > 0 ? "text-sm" : ""}`;
  let t7;
  if ($[10] !== org.name || $[11] !== t6) {
    t7 = <span className={t6}>{org.name}</span>;
    $[10] = org.name;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== isSelected) {
    t8 = isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />;
    $[13] = isSelected;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="flex items-center gap-2">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== org.access_type) {
    t10 = org.access_type === "inherited" && <span className="text-xs text-muted-foreground">via parent org</span>;
    $[18] = org.access_type;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== org.access_type) {
    t11 = org.access_type === "global_admin" && <span className="text-xs text-muted-foreground">platform access</span>;
    $[20] = org.access_type;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11 || $[24] !== t9) {
    t12 = <div className="flex-1 min-w-0">{t9}{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t9;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t4 || $[28] !== t5) {
    t13 = <div className="flex items-center gap-3">{t4}{t5}{t12}</div>;
    $[26] = t12;
    $[27] = t4;
    $[28] = t5;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== onSelect || $[31] !== t1 || $[32] !== t13 || $[33] !== t3) {
    t14 = <button onClick={onSelect} className={t1} style={t3}>{t13}</button>;
    $[30] = onSelect;
    $[31] = t1;
    $[32] = t13;
    $[33] = t3;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  return t14;
}